import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';

import { AuthGuard } from '@sa/core/auth/auth.guard';
import { WorkitemLinkGuard } from '@sa/core/guards/workitemlink.guard';
import { ErrorComponent } from '@sa/shared/components/error/error.component';
import { UserUnauthorizeComponent } from '@sa/shared/components/user-unauthorize/user-unauthorize.component';
import { FeatureNames } from '@sa/shared/constants/feature-names';
import { TenantGuard } from '@sa/core/guards/tenant.gaurd';
import { DeliverableManagementViewComponent } from '@sa/pages/deliverable-management-view/deliverable-management-view.component';
import { clientAdminRoleGuard } from '@sa/core/guards/client-Admin-role-guard';
import { platformAdminRoleGuard } from '@sa/core/guards/platform-admin-role-guard';
import { agentsMonitorRoleGuard } from '@sa/core/guards/agents-monitor-role-guard';
import { dataManagementHubRoleGuard } from '@sa/core/guards/data-management-hub-role-guard';
import { devEnvironmentGuard } from '@sa/core/guards/dev-environment-guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/login/login.component').then(c => c.LoginComponent),
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./pages/dashboard/dashboard.component').then(
        c => c.DashboardComponent
      ),
    canActivate: [MsalGuard, TenantGuard],
    data: { breadcrumb: 'Dashboard' },
    children: [
      {
        path: 'fs-caption-view',
        loadComponent: () =>
          import('./pages/fs-caption-view/fs-caption-view.component').then(
            c => c.FsCaptionViewComponent
          ),
        data: {
          breadcrumb: 'FS Caption View',
          featureNames: [FeatureNames.VIEW_FS_GRID],
        },
        canActivate: [MsalGuard, AuthGuard],
        children: [
          {
            path: 'focus-area',
            loadComponent: () =>
              import('./pages/focus-area/focus-area.component').then(
                c => c.FocusAreaComponent
              ),
            canActivate: [MsalGuard, AuthGuard],
            data: { breadcrumb: 'Focus Area' },
          },
          {
            path: 'work-detail',
            loadComponent: () =>
              import('./pages/work-detail/work-detail.component').then(
                c => c.WorkDetailComponent
              ),
            canActivate: [MsalGuard, AuthGuard],
            data: { breadcrumb: 'Work Detail' },
          },
        ],
      },
    ],
  },
  {
    path: 'invoice-extractor-view',
    loadComponent: () =>
      import(
        './pages/invoice-extraction-view/invoice-extraction-view.component'
      ).then(c => c.InvoiceExtractionViewComponent),
    canActivate: [MsalGuard, TenantGuard],
    data: { breadcrumb: 'Document Extractor' },
  },
  // Flattened routes for direct access
  {
    path: 'focus-area',
    loadComponent: () =>
      import('./pages/focus-area/focus-area.component').then(
        c => c.FocusAreaComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: { breadcrumb: 'Focus Area' },
  },
  {
    path: 'work-detail',
    loadComponent: () =>
      import('./pages/work-detail/work-detail.component').then(
        c => c.WorkDetailComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: { breadcrumb: 'Work Detail' },
  },
  {
    path: 'deliverable',
    loadComponent: () =>
      import('./pages/deliverables/deliverables.component').then(
        c => c.DeliverablesComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: {
      breadcrumb: 'Deliverable',
    },
  },
  {
    path: 'client-management',
    data: { breadcrumb: 'Client Management' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './components/client-management-feature/client-management/client-management.component'
          ).then(c => c.ClientManagementComponent),
        canActivate: [MsalGuard, AuthGuard],
        data: {},
      },
      {
        path: ':id/entity-management',
        loadComponent: () =>
          import(
            './components/client-management-feature/entity-management/entity-management.component'
          ).then(c => c.EntityManagementComponent),
        canActivate: [MsalGuard],
        data: { breadcrumb: 'Entity Management' },
      },
    ],
  },
  {
    path: 'user-management',
    loadComponent: () =>
      import(
        './pages/user-management-view/user-management-view.component'
      ).then(c => c.UserManagementViewComponent),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'User Management' },
  },
  {
    path: 'reference-data-management',
    loadComponent: () =>
      import(
        './pages/reference-data-management/reference-data-management.component'
      ).then(c => c.ReferenceDataManagementComponent),
    canActivate: [MsalGuard, AuthGuard, clientAdminRoleGuard],
    data: { breadcrumb: 'Client Reference Data Management' },
  },
  {
    path: 'platform-reference-data-management',
    loadComponent: () =>
      import(
        './pages/platform-reference-data-management/platform-reference-data-management.component'
      ).then(c => c.PlatformReferenceDataManagementComponent),
    canActivate: [MsalGuard, AuthGuard, platformAdminRoleGuard],
    data: { breadcrumb: 'Platform Reference Data Management' },
  },
  {
    path: 'deliverable-management-view',
    component: DeliverableManagementViewComponent,
    canActivate: [MsalGuard, AuthGuard, clientAdminRoleGuard],
    data: { breadcrumb: 'Deliverable Management' },
  },
  {
    path: 'rule-workspace',
    loadComponent: () =>
      import('./pages/rule-workspace/rule-workspace.component').then(
        c => c.RuleWorkspaceComponent
      ),
    canActivate: [MsalGuard, AuthGuard, clientAdminRoleGuard],
    data: { breadcrumb: 'Rule Workspace' },
  },
  {
    path: 'compliance-calendar',
    data: {
      breadcrumb: 'Compliance Calendar',
    },
    canActivate: [MsalGuard, AuthGuard, clientAdminRoleGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './pages/compliance-calendar-view/compliance-calendar-view.component'
          ).then(c => c.ComplianceCalendarViewComponent),
        data: {},
      },
      {
        path: 'generate-calendar',
        loadComponent: () =>
          import(
            './pages/compliance-calendar-workflow-view/compliance-calendar-workflow-view.component'
          ).then(c => c.ComplianceCalendarWorkflowViewComponent),
        data: {
          breadcrumb: 'Generate Compliance Calendar',
        },
      },
      {
        path: 'calendar-finalization',
        loadComponent: () =>
          import(
            './pages/compliance-calendar-workflow-view/compliance-calendar-workflow-view.component'
          ).then(c => c.ComplianceCalendarWorkflowViewComponent),
        data: {
          breadcrumb: 'Compliance Calendar Finalization',
        },
      },
    ],
  },
  {
    path: 'deliverable',
    data: {
      breadcrumb: 'Deliverable',
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/deliverables/deliverables.component').then(
            c => c.DeliverablesComponent
          ),
        data: {},
      },
      {
        path: 'milestone-details',
        loadComponent: () =>
          import(
            './components/milestone-details/milestone-details.component'
          ).then(c => c.MilestoneDetailsComponent),
        data: {
          breadcrumb: 'Milestone',
        },
      },
      {
        path: 'task-details',
        loadComponent: () =>
          import(
            './components/milestone-details/milestone-details.component'
          ).then(c => c.MilestoneDetailsComponent),
        data: {
          breadcrumb: 'Task',
        },
      },
    ],
  },
  {
    path: 'data-management-hub',
    loadComponent: () =>
      import('./pages/data-management-hub/data-management-hub.component').then(
        c => c.DataManagementHubComponent
      ),
    canActivate: [MsalGuard, TenantGuard, dataManagementHubRoleGuard],
    data: { breadcrumb: 'Data Management Hub' },
  },
  {
    path: 'agents',
    loadComponent: () =>
      import('./pages/agents/agents.component').then(c => c.AgentsComponent),
    canActivate: [MsalGuard, AuthGuard, platformAdminRoleGuard],
    data: { breadcrumb: 'Agents' },
  },
  {
    path: 'agents/monitor',
    loadComponent: () =>
      import('./pages/agents-monitor/agents-monitor.component').then(
        c => c.AgentsMonitorComponent
      ),
    canActivate: [MsalGuard, AuthGuard, agentsMonitorRoleGuard],
    data: { breadcrumb: 'Agents Monitor' },
  },
  {
    path: 'foundry-agent-config',
    loadComponent: () =>
      import('./pages/foundry-agent-config/foundry-agent-config.component').then(
        c => c.FoundryAgentConfigComponent
      ),
    // agentsMonitorRoleGuard already allows PlatformAdmin + ClientAdmin, reused here to avoid a duplicate guard
    canActivate: [MsalGuard, AuthGuard, devEnvironmentGuard, agentsMonitorRoleGuard],
    data: { breadcrumb: 'Foundry Agents Configuration' },
  },
  {
    path: 'foundry-agent-config/:agentGuid',
    loadComponent: () =>
      import(
        './pages/foundry-agent-config/agent-config-detail/agent-config-detail.component'
      ).then(c => c.AgentConfigDetailComponent),
    canActivate: [MsalGuard, AuthGuard, devEnvironmentGuard, agentsMonitorRoleGuard],
    data: { breadcrumb: 'Agent Configuration' },
  },
  {
    path: 'service-health',
    loadComponent: () =>
      import('./pages/service-health/service-health.component').then(
        c => c.ServiceHealthComponent
      ),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'System Health' },
  },
  { path: 'error', component: ErrorComponent, data: { breadcrumb: 'Error' } },
  {
    path: 'no-auth',
    component: UserUnauthorizeComponent,
    data: { breadcrumb: 'Unauthorized' },
  },
  {
    path: '**',
    redirectTo: 'error',
  },
];
